/**
 * @file ProtectedRoute.tsx
 * @description Route guard: if there's no access token AND the initial
 *   bootstrap refresh has completed, redirect to /login carrying the
 *   originally requested path (`state.from`) so LoginPage can return the
 *   user there after auth — deep links from quote emails land on the
 *   quote, not the dashboard. While bootstrap is pending we render a
 *   light skeleton — avoids a redirect flash on page reload where the
 *   cookie-backed refresh succeeds.
 * @module Components
 * @security Client-side only. The real authorization happens at the API
 *   on every request. Never trust this guard alone. LoginPage validates
 *   `state.from` is an in-app path before navigating (no open redirect).
 */

import type { JSX, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, useIsAuthenticated } from '../../stores/auth.store';

export function ProtectedRoute({ children }: { children: ReactNode }): JSX.Element {
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const authed = useIsAuthenticated();
  const location = useLocation();
  if (!bootstrapped) {
    return (
      <main
        aria-busy="true"
        className="min-h-screen flex items-center justify-center text-sm text-slate-500"
      >
        …
      </main>
    );
  }
  if (!authed) {
    const from = `${location.pathname}${location.search}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }
  return <>{children}</>;
}
